/**
 * TanStack Query hooks for the Categories admin feature.
 *
 * The admin endpoint surface (list with `type` filter, create, update,
 * delete) is typed via the generated OpenAPI. The reorder endpoint and
 * the `includeInactive` query param aren't published yet — for reorder
 * we use `as never` casts; for `includeInactive` we filter client-side
 * (the legacy admin already received all categories from this endpoint
 * regardless of active state). TODO: simplify once OpenAPI catches up.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';

export type CategoryResponse = components['schemas']['CategoryResponse'];
export type CreateCategoryRequest = components['schemas']['CreateCategoryRequest'];
export type UpdateCategoryRequest = components['schemas']['UpdateCategoryRequest'];

export type CategoryType = 'MERCHANT' | 'PRODUCT';

export interface CategoryListOpts {
  type: CategoryType;
  includeInactive: boolean;
}

export const categoriesKeys = {
  all: ['categories'] as const,
  list: (opts: CategoryListOpts) => [...categoriesKeys.all, 'list', opts] as const,
};

export function useCategoriesList(
  opts: CategoryListOpts,
): UseQueryResult<CategoryResponse[]> {
  return useQuery({
    queryKey: categoriesKeys.list(opts),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/categories', {
        params: { query: { type: opts.type } },
      });
      if (error) throw error as Error;
      const list = data ?? [];
      // includeInactive filtering is client-side until the backend exposes
      // the parameter in the OpenAPI schema.
      return opts.includeInactive ? list : list.filter((c) => c.isActive !== false);
    },
  });
}

export function useCreateCategory(): UseMutationResult<
  CategoryResponse,
  Error,
  CreateCategoryRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const { data, error } = await api.POST('/api/v1/admin/categories', { body });
      if (error) throw error as Error;
      return data as CategoryResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: categoriesKeys.all });
    },
  });
}

export function useUpdateCategory(): UseMutationResult<
  CategoryResponse,
  Error,
  { id: string; body: UpdateCategoryRequest }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const { data, error } = await api.PUT('/api/v1/admin/categories/{id}', {
        params: { path: { id } },
        body,
      });
      if (error) throw error as Error;
      return data as CategoryResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: categoriesKeys.all });
    },
  });
}

export function useDeleteCategory(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { error } = await api.DELETE('/api/v1/admin/categories/{id}', {
        params: { path: { id } },
      });
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: categoriesKeys.all });
    },
  });
}

/**
 * Reorder categories. Endpoint not yet typed — `as never` cast.
 * `parentId` may be passed for child reorder within a parent.
 * TODO: drop cast once OpenAPI publishes the route.
 */
export function useReorderCategories(): UseMutationResult<
  void,
  Error,
  { ids: string[]; parentId?: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, parentId }) => {
      const body: { ids: string[]; parentId?: string } = { ids };
      if (parentId) body.parentId = parentId;
      const { error } = await api.POST(
        '/api/v1/admin/categories/reorder' as never,
        { body } as never,
      );
      if (error) throw error as Error;
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: categoriesKeys.all });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: categoriesKeys.all });
    },
  });
}
